import type { CreateSignParams, DocumentsParams, SearchSignsParams } from '@repo/api-adapters/docusign';
import {
    createSignRequestWithTemplate,
    getAccountTemplates,
    getSignRequestDocuments,
    getSignRequestWithFilters,
} from '@repo/api-adapters/docusign';
import type { AccountTemplates } from '@repo/api-types/docusign';
import { type SignRequest } from '@repo/api-types/docusign';
import type { UseAccountTemplatesProps, UseSignRequestsHookProps } from '@repo/types/docusign';
import { useMutation, useQuery } from '@tanstack/react-query';

import { queryClient } from '../lib/react-query';
import { API } from './utils.api';

export const useCreateSignRequestMutation = (basePath: string) => {
    return useMutation({
        mutationFn: async (data: CreateSignParams) => {
            return createSignRequestWithTemplate(data, basePath);
        },
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [API.signRequest] });
        },
    });
};

export const useSignRequests = ({ orderId, fromDate, basePath, enabled }: UseSignRequestsHookProps) => {
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    return useQuery<SignRequest[]>({
        queryKey: [API.signRequest, orderId, fromDate],
        queryFn: () => {
            const apiParams: SearchSignsParams = {
                fromDate,
                customField: `referenceNumber=${orderId}`,
            };
            return getSignRequestWithFilters(apiParams, basePath);
        },
        enabled: enabled,
    });
};

export const useViewSignRequestDocuments = (basePath: string) => {
    return useMutation({
        mutationFn: async (params: DocumentsParams) => {
            return getSignRequestDocuments(params, basePath);
        },
    });
};

export const useAccountTemplates = ({ basePath, enabled = true }: UseAccountTemplatesProps) => {
    const query = useQuery<AccountTemplates>({
        queryKey: [API.accountTemplates, basePath],
        queryFn: () => {
            return getAccountTemplates(basePath);
        },
        enabled: enabled,
    });

    return {
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        hasError: query.isError,
        data: query.data,
    };
};
